import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/apiConfig';
import { AcademicCapIcon, PencilIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, VideoCameraIcon, DocumentTextIcon, ChatBubbleLeftRightIcon, PlusIcon, CloudArrowUpIcon, QuestionMarkCircleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import CourseForm from '../components/forms/CourseForm';
import ModuleForm from '../components/forms/ModuleForm';
import LessonForm from '../components/forms/LessonForm';
import EditModuleForm from '../components/forms/EditModuleForm';
import EditLessonForm from '../components/forms/EditLessonForm';
import { Course } from '../types/course';
import { Button, Alert, Spinner, Badge } from './ui';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

const CourseList: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | undefined>(undefined);
  const [expandedModules, setExpandedModules] = useState<Record<number, boolean>>({});

  // Module/Lesson management state
  const [isModuleFormOpen, setIsModuleFormOpen] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [isLessonFormOpen, setIsLessonFormOpen] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null);
  const [isEditModuleFormOpen, setIsEditModuleFormOpen] = useState(false);
  const [isEditLessonFormOpen, setIsEditLessonFormOpen] = useState(false);
  const [selectedLessonId, setSelectedLessonId] = useState<number | null>(null);

  // Bulk upload state
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [bulkUploadModuleId, setBulkUploadModuleId] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [totalFiles, setTotalFiles] = useState<number>(0);

  // Hover states for interactive elements
  const [hoveredCourseCard, setHoveredCourseCard] = useState<number | null>(null);
  const [hoveredEditBtn, setHoveredEditBtn] = useState<number | null>(null);
  const [hoveredDeleteBtn, setHoveredDeleteBtn] = useState<number | null>(null);
  const [hoveredModuleBtn, setHoveredModuleBtn] = useState<number | null>(null);
  const [hoveredModuleEditBtn, setHoveredModuleEditBtn] = useState<number | null>(null);
  const [hoveredModuleDeleteBtn, setHoveredModuleDeleteBtn] = useState<number | null>(null);
  const [hoveredLesson, setHoveredLesson] = useState<number | null>(null);
  const [hoveredLessonEditBtn, setHoveredLessonEditBtn] = useState<number | null>(null);
  const [hoveredLessonDeleteBtn, setHoveredLessonDeleteBtn] = useState<number | null>(null);
  const [hoveredCancelBtn, setHoveredCancelBtn] = useState(false);
  const [hoveredDownloadBtn, setHoveredDownloadBtn] = useState<number | null>(null);
  const [downloadingCourseId, setDownloadingCourseId] = useState<number | null>(null);

  const handleDownloadTranscripts = async (e: React.MouseEvent, courseId: number) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      setDownloadingCourseId(courseId);
      const response = await apiClient.get(`/api/courses/${courseId}/download_transcripts/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition
        ? disposition.split('filename="')[1]?.replace('"', '') || 'transcripts.zip'
        : 'transcripts.zip';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      if (error.response?.status === 404) {
        alert('No transcripts found for this course.');
      } else {
        console.error('Error downloading transcripts:', error);
        alert('Failed to download transcripts');
      }
    } finally {
      setDownloadingCourseId(null);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/courses/');
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load courses data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (e: React.MouseEvent, course: Course) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedCourse(course);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (e: React.MouseEvent, courseId: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this course?')) {
      return;
    }
    try {
      const response = await apiClient.delete(`/api/courses/${courseId}/`);

      if (response.status >= 200 && response.status < 300) {
        setCourses(courses.filter(course => course.id !== courseId));
      } else {
        const errorData = response.data;
        console.error('Delete failed:', errorData);
        alert('Failed to delete course: ' + (errorData?.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Failed to delete course');
    }
  };

  const handleAddModule = (e: React.MouseEvent, courseId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedCourseId(courseId);
    setIsModuleFormOpen(true);
  };

  const handleEditModule = (e: React.MouseEvent, moduleId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedModuleId(moduleId);
    setIsEditModuleFormOpen(true);
  };

  const handleDeleteModule = async (e: React.MouseEvent, moduleId: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this module? This will also delete all lessons in this module.')) {
      return;
    }
    try {
      await apiClient.delete(`/api/modules/${moduleId}/`);
      await fetchData();
    } catch (error) {
      console.error('Error deleting module:', error);
      alert('Failed to delete module');
    }
  };

  const handleAddLesson = (e: React.MouseEvent, moduleId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedModuleId(moduleId);
    setIsLessonFormOpen(true);
  };

  const handleEditLesson = (e: React.MouseEvent, lessonId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedLessonId(lessonId);
    setIsEditLessonFormOpen(true);
  };

  const handleDeleteLesson = async (e: React.MouseEvent, lessonId: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this lesson?')) {
      return;
    }
    try {
      await apiClient.delete(`/api/lessons/${lessonId}/`);
      await fetchData();
    } catch (error) {
      console.error('Error deleting lesson:', error);
      alert('Failed to delete lesson');
    }
  };

  const handleBulkUpload = (e: React.MouseEvent, moduleId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setBulkUploadModuleId(moduleId);
    setIsBulkUploadOpen(true);
  };

  const handleBulkUploadSubmit = async (files: FileList) => {
    if (!bulkUploadModuleId || files.length === 0) return;

    const MAX_FILES = 100;
    if (files.length > MAX_FILES) {
      alert(`Too many files! Maximum ${MAX_FILES} videos per batch. You selected ${files.length}.\n\nPlease select fewer files.`);
      return;
    }

    setIsUploading(true);
    setUploadStatus('Creating lessons...');
    setUploadProgress({});
    setTotalFiles(files.length);

    try {
      const filesData = Array.from(files).map(file => ({
        filename: file.name,
        content_type: file.type || 'video/mp4'
      }));

      const urlResponse = await apiClient.post(
        `/api/modules/${bulkUploadModuleId}/generate_upload_urls/`,
        { files: filesData }
      );

      const { uploads, errors } = urlResponse.data;

      if (errors && errors.length > 0) {
        console.error('Errors generating URLs:', errors);
      }

      if (!uploads || uploads.length === 0) {
        alert('Failed to generate upload URLs. Please try again.');
        setIsUploading(false);
        return;
      }

      setUploadStatus(`Uploading ${uploads.length} videos to S3...`);

      const uploadPromises = uploads.map(async (uploadInfo: any, index: number) => {
        const file = files[index];
        const fileKey = uploadInfo.filename;

        try {
          await fetch(uploadInfo.upload_url, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': uploadInfo.content_type || file.type,
            },
          });

          setUploadProgress(prev => ({ ...prev, [fileKey]: 100 }));

          await apiClient.post(`/api/lessons/${uploadInfo.lesson_id}/complete_upload/`);

          return { success: true, filename: fileKey };
        } catch (error) {
          console.error(`Error uploading ${fileKey}:`, error);
          return { success: false, filename: fileKey, error };
        }
      });

      const results = await Promise.all(uploadPromises);
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      if (failCount > 0) {
        alert(`Upload complete!\n\nSuccessful: ${successCount}\nFailed: ${failCount}\n\nFailed files may need to be re-uploaded.`);
      } else {
        alert(`Successfully uploaded ${successCount} videos!`);
      }

      setIsBulkUploadOpen(false);
      setBulkUploadModuleId(null);
      setUploadProgress({});
      setUploadStatus('');
      await fetchData();
    } catch (error: any) {
      console.error('Error in bulk upload:', error);
      alert(`Failed to upload videos: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const toggleModule = (moduleId: number) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const getStatusVariant = (status: string): 'success' | 'info' | 'warning' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'info';
      case 'paused':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Helper function to check if lesson has quiz (handles both string and JSON formats)
  const hasQuiz = (quiz: any): boolean => {
    if (!quiz) return false;
    if (typeof quiz === 'string') return quiz.trim().length > 0;
    if (typeof quiz === 'object') return Object.keys(quiz).length > 0;
    return false;
  };

  const getCourseStats = (course: Course) => {
    const totalLessons = course.modules.reduce((total, module) => total + module.lessons.length, 0);
    const lessonsWithVideo = course.modules.reduce((total, module) =>
      total + module.lessons.filter(lesson => lesson.video_url || lesson.video_file).length, 0);
    const lessonsWithTranscript = course.modules.reduce((total, module) =>
      total + module.lessons.filter(lesson => lesson.transcript && lesson.transcript.trim()).length, 0);
    const lessonsWithSummary = course.modules.reduce((total, module) =>
      total + module.lessons.filter(lesson => lesson.summary && lesson.summary.trim()).length, 0);
    const lessonsWithQuiz = course.modules.reduce((total, module) =>
      total + module.lessons.filter(lesson => hasQuiz(lesson.quiz)).length, 0);

    return {
      totalLessons,
      lessonsWithVideo,
      lessonsWithTranscript,
      lessonsWithSummary,
      lessonsWithQuiz
    };
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '128px' }}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">{error}</Alert>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
        gap: SPACING[16]
      }}>
        {courses.map((course) => (
          <div
            key={course.id}
            style={{
              backgroundColor: COLORS.bg.panel,
              borderRadius: RADIUS,
              overflow: 'hidden',
              transition: 'box-shadow 200ms',
              boxShadow: hoveredCourseCard === course.id ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
            }}
            onMouseEnter={() => setHoveredCourseCard(course.id)}
            onMouseLeave={() => setHoveredCourseCard(null)}
          >
            <div style={{ padding: SPACING[16] }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
                  <AcademicCapIcon style={{ width: '24px', height: '24px', color: COLORS.accent.default }} />
                  <div>
                    <h3 style={{
                      fontSize: TYPOGRAPHY.headings.sizes.h2,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: COLORS.text.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>{course.title}</h3>
                    <p style={{
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      color: COLORS.text.secondary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>{course.source}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                  <Badge variant={getStatusVariant(course.status)} size="sm">
                    {course.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleDownloadTranscripts(e, course.id)}
                    disabled={downloadingCourseId === course.id}
                    style={{
                      padding: SPACING[4],
                      height: 'auto',
                      borderRadius: '50%',
                      backgroundColor: hoveredDownloadBtn === course.id ? COLORS.bg.hover : 'transparent'
                    }}
                    title="Download Transcripts"
                    onMouseEnter={() => setHoveredDownloadBtn(course.id)}
                    onMouseLeave={() => setHoveredDownloadBtn(null)}
                  >
                    <ArrowDownTrayIcon style={{ width: '20px', height: '20px', color: COLORS.text.secondary }} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleEdit(e, course)}
                    style={{
                      padding: SPACING[4],
                      height: 'auto',
                      borderRadius: '50%',
                      backgroundColor: hoveredEditBtn === course.id ? COLORS.bg.hover : 'transparent'
                    }}
                    onMouseEnter={() => setHoveredEditBtn(course.id)}
                    onMouseLeave={() => setHoveredEditBtn(null)}
                  >
                    <PencilIcon style={{ width: '20px', height: '20px', color: COLORS.text.secondary }} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleDelete(e, course.id)}
                    style={{
                      padding: SPACING[4],
                      height: 'auto',
                      borderRadius: '50%',
                      backgroundColor: hoveredDeleteBtn === course.id ? COLORS.bg.hover : 'transparent'
                    }}
                    title="Delete Course"
                    onMouseEnter={() => setHoveredDeleteBtn(course.id)}
                    onMouseLeave={() => setHoveredDeleteBtn(null)}
                  >
                    <TrashIcon style={{ width: '20px', height: '20px', color: COLORS.status.error }} />
                  </Button>
                </div>
              </div>

              {course.description && (
                <p style={{
                  marginTop: SPACING[8],
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  color: COLORS.text.secondary,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}>{course.description}</p>
              )}

              <div style={{ marginTop: SPACING[16], display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
                {/* Add Module Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => handleAddModule(e, course.id)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: SPACING[4],
                    fontSize: TYPOGRAPHY.body.sizes.small
                  }}
                  fullWidth
                >
                  <PlusIcon style={{ width: '16px', height: '16px' }} />
                  Add Module
                </Button>

                {course.modules.map((module) => (
                  <div key={module.id} style={{ border: `1px solid ${COLORS.border.default}`, borderRadius: RADIUS }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: SPACING[8],
                      backgroundColor: COLORS.bg.base
                    }}>
                      <Button
                        variant="ghost"
                        onClick={(e) => {
                          e.preventDefault();
                          toggleModule(module.id);
                        }}
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          height: 'auto',
                          backgroundColor: hoveredModuleBtn === module.id ? COLORS.bg.hover : 'transparent'
                        }}
                        onMouseEnter={() => setHoveredModuleBtn(module.id)}
                        onMouseLeave={() => setHoveredModuleBtn(null)}
                      >
                        <span style={{
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          color: COLORS.text.primary
                        }}>{module.title}</span>
                        {expandedModules[module.id] ? (
                          <ChevronUpIcon style={{ width: '16px', height: '16px', color: COLORS.text.tertiary }} />
                        ) : (
                          <ChevronDownIcon style={{ width: '16px', height: '16px', color: COLORS.text.tertiary }} />
                        )}
                      </Button>
                      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[4], marginLeft: SPACING[8] }}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleEditModule(e, module.id)}
                          style={{
                            padding: SPACING[4],
                            height: 'auto',
                            backgroundColor: hoveredModuleEditBtn === module.id ? COLORS.bg.hover : 'transparent'
                          }}
                          title="Edit Module"
                          onMouseEnter={() => setHoveredModuleEditBtn(module.id)}
                          onMouseLeave={() => setHoveredModuleEditBtn(null)}
                        >
                          <PencilIcon style={{ width: '16px', height: '16px', color: COLORS.text.secondary }} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleDeleteModule(e, module.id)}
                          style={{
                            padding: SPACING[4],
                            height: 'auto',
                            backgroundColor: hoveredModuleDeleteBtn === module.id ? COLORS.bg.hover : 'transparent'
                          }}
                          title="Delete Module"
                          onMouseEnter={() => setHoveredModuleDeleteBtn(module.id)}
                          onMouseLeave={() => setHoveredModuleDeleteBtn(null)}
                        >
                          <TrashIcon style={{ width: '16px', height: '16px', color: COLORS.status.error }} />
                        </Button>
                      </div>
                    </div>
                    {expandedModules[module.id] && (
                      <div style={{
                        borderTop: `1px solid ${COLORS.border.default}`,
                        padding: SPACING[8],
                        display: 'flex',
                        flexDirection: 'column',
                        gap: SPACING[4]
                      }}>
                        {/* Module Action Buttons */}
                        <div style={{
                          display: 'flex',
                          gap: SPACING[8],
                          marginBottom: SPACING[8],
                          paddingBottom: SPACING[8],
                          borderBottom: `1px solid ${COLORS.border.default}`
                        }}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => handleAddLesson(e, module.id)}
                            style={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: SPACING[4],
                              fontSize: TYPOGRAPHY.body.sizes.tiny
                            }}
                          >
                            <PlusIcon style={{ width: '14px', height: '14px' }} />
                            Add Lesson
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => handleBulkUpload(e, module.id)}
                            style={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: SPACING[4],
                              fontSize: TYPOGRAPHY.body.sizes.tiny
                            }}
                          >
                            <CloudArrowUpIcon style={{ width: '14px', height: '14px' }} />
                            Bulk Upload
                          </Button>
                        </div>

                        {/* Lessons */}
                        {module.lessons.length === 0 ? (
                          <div style={{
                            textAlign: 'center',
                            color: COLORS.text.tertiary,
                            fontSize: TYPOGRAPHY.body.sizes.tiny,
                            paddingTop: SPACING[8],
                            paddingBottom: SPACING[8]
                          }}>
                            No lessons yet. Add one above.
                          </div>
                        ) : (
                          module.lessons.map((lesson) => (
                            <div
                              key={lesson.id}
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(`/lessons/${lesson.id}`);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                fontSize: TYPOGRAPHY.body.sizes.small,
                                color: COLORS.text.secondary,
                                paddingTop: SPACING[4],
                                paddingBottom: SPACING[4],
                                paddingLeft: SPACING[8],
                                paddingRight: SPACING[8],
                                backgroundColor: hoveredLesson === lesson.id ? '#EFF6FF' : 'transparent',
                                borderRadius: RADIUS,
                                cursor: 'pointer'
                              }}
                              onMouseEnter={() => setHoveredLesson(lesson.id)}
                              onMouseLeave={() => setHoveredLesson(null)}
                            >
                              <span style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: hoveredLesson === lesson.id ? COLORS.accent.default : COLORS.text.secondary,
                                flex: 1,
                                marginRight: SPACING[8]
                              }}>{lesson.title}</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                                {/* Status Icons */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
                                  {/* Video Status */}
                                  {lesson.video_url || lesson.video_file ? (
                                    <VideoCameraIcon style={{ width: '16px', height: '16px', color: COLORS.status.success }} title="Has Video" />
                                  ) : (
                                    <VideoCameraIcon style={{ width: '16px', height: '16px', color: '#F87171' }} title="No Video" />
                                  )}
                                  {/* Transcript Status */}
                                  {lesson.transcript && lesson.transcript.trim() ? (
                                    <DocumentTextIcon style={{ width: '16px', height: '16px', color: COLORS.status.success }} title="Has Transcript" />
                                  ) : (
                                    <DocumentTextIcon style={{ width: '16px', height: '16px', color: '#F87171' }} title="No Transcript" />
                                  )}
                                  {/* Summary Status */}
                                  {lesson.summary && lesson.summary.trim() ? (
                                    <ChatBubbleLeftRightIcon style={{ width: '16px', height: '16px', color: COLORS.status.success }} title="Has Summary" />
                                  ) : (
                                    <ChatBubbleLeftRightIcon style={{ width: '16px', height: '16px', color: '#F87171' }} title="No Summary" />
                                  )}
                                  {/* Quiz Status */}
                                  {hasQuiz(lesson.quiz) ? (
                                    <QuestionMarkCircleIcon style={{ width: '16px', height: '16px', color: COLORS.status.success }} title="Has Quiz" />
                                  ) : (
                                    <QuestionMarkCircleIcon style={{ width: '16px', height: '16px', color: '#F87171' }} title="No Quiz" />
                                  )}
                                </div>
                                {lesson.is_completed && (
                                  <Badge variant="success" size="sm">Completed</Badge>
                                )}
                                <div style={{
                                  display: 'flex',
                                  gap: SPACING[4],
                                  opacity: hoveredLesson === lesson.id ? 1 : 0,
                                  transition: 'opacity 150ms'
                                }}>
                                  <button
                                    onClick={(e) => handleEditLesson(e, lesson.id)}
                                    style={{
                                      padding: SPACING[4],
                                      backgroundColor: hoveredLessonEditBtn === lesson.id ? COLORS.border.hover : 'transparent',
                                      borderRadius: RADIUS,
                                      border: 'none',
                                      cursor: 'pointer'
                                    }}
                                    title="Edit Lesson"
                                    onMouseEnter={() => setHoveredLessonEditBtn(lesson.id)}
                                    onMouseLeave={() => setHoveredLessonEditBtn(null)}
                                  >
                                    <PencilIcon style={{ width: '14px', height: '14px', color: COLORS.text.secondary }} />
                                  </button>
                                  <button
                                    onClick={(e) => handleDeleteLesson(e, lesson.id)}
                                    style={{
                                      padding: SPACING[4],
                                      backgroundColor: hoveredLessonDeleteBtn === lesson.id ? '#FEE2E2' : 'transparent',
                                      borderRadius: RADIUS,
                                      border: 'none',
                                      cursor: 'pointer'
                                    }}
                                    title="Delete Lesson"
                                    onMouseEnter={() => setHoveredLessonDeleteBtn(lesson.id)}
                                    onMouseLeave={() => setHoveredLessonDeleteBtn(null)}
                                  >
                                    <TrashIcon style={{ width: '14px', height: '14px', color: COLORS.status.error }} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{
                marginTop: SPACING[12],
                paddingTop: SPACING[12],
                borderTop: `1px solid ${COLORS.border.default}`
              }}>
                <div style={{
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[8]
                }}>
                  {course.modules.length} module{course.modules.length !== 1 ? 's' : ''} •{' '}
                  {getCourseStats(course).totalLessons} lesson{getCourseStats(course).totalLessons !== 1 ? 's' : ''}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING[8] }}>
                  {(() => {
                    const stats = getCourseStats(course);
                    return (
                      <>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          paddingLeft: SPACING[8],
                          paddingRight: SPACING[8],
                          paddingTop: SPACING[4],
                          paddingBottom: SPACING[4],
                          borderRadius: RADIUS,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          backgroundColor: '#EFF6FF',
                          color: '#1D4ED8'
                        }}>
                          <VideoCameraIcon style={{ width: '14px', height: '14px', marginRight: SPACING[4] }} />
                          {stats.lessonsWithVideo}/{stats.totalLessons} Videos
                        </div>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          paddingLeft: SPACING[8],
                          paddingRight: SPACING[8],
                          paddingTop: SPACING[4],
                          paddingBottom: SPACING[4],
                          borderRadius: RADIUS,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          backgroundColor: '#F5F3FF',
                          color: '#6D28D9'
                        }}>
                          <DocumentTextIcon style={{ width: '14px', height: '14px', marginRight: SPACING[4] }} />
                          {stats.lessonsWithTranscript}/{stats.totalLessons} Transcripts
                        </div>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          paddingLeft: SPACING[8],
                          paddingRight: SPACING[8],
                          paddingTop: SPACING[4],
                          paddingBottom: SPACING[4],
                          borderRadius: RADIUS,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          backgroundColor: '#ECFDF5',
                          color: '#047857'
                        }}>
                          <ChatBubbleLeftRightIcon style={{ width: '14px', height: '14px', marginRight: SPACING[4] }} />
                          {stats.lessonsWithSummary}/{stats.totalLessons} Summaries
                        </div>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          paddingLeft: SPACING[8],
                          paddingRight: SPACING[8],
                          paddingTop: SPACING[4],
                          paddingBottom: SPACING[4],
                          borderRadius: RADIUS,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          backgroundColor: '#FFF7ED',
                          color: '#C2410C'
                        }}>
                          <QuestionMarkCircleIcon style={{ width: '14px', height: '14px', marginRight: SPACING[4] }} />
                          {stats.lessonsWithQuiz}/{stats.totalLessons} Quizzes
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Course Form */}
      <CourseForm
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCourse(undefined);
        }}
        onCourseCreated={fetchData}
        initialCourse={selectedCourse}
        isEditMode={!!selectedCourse}
      />

      {/* Module Forms */}
      <ModuleForm
        isOpen={isModuleFormOpen}
        onClose={() => setIsModuleFormOpen(false)}
        onModuleCreated={fetchData}
        courseId={selectedCourseId || 0}
      />

      <EditModuleForm
        isOpen={isEditModuleFormOpen}
        onClose={() => setIsEditModuleFormOpen(false)}
        onModuleUpdated={fetchData}
        moduleId={selectedModuleId || 0}
      />

      {/* Lesson Forms */}
      <LessonForm
        isOpen={isLessonFormOpen}
        onClose={() => setIsLessonFormOpen(false)}
        onLessonCreated={fetchData}
        moduleId={selectedModuleId || 0}
      />

      <EditLessonForm
        isOpen={isEditLessonFormOpen}
        onClose={() => setIsEditLessonFormOpen(false)}
        onLessonUpdated={fetchData}
        lessonId={selectedLessonId || 0}
      />

      {/* Bulk Upload Modal */}
      {isBulkUploadOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{
            backgroundColor: COLORS.bg.panel,
            padding: SPACING[24],
            borderRadius: RADIUS,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            width: '100%',
            maxWidth: '448px'
          }}>
            <h2 style={{
              fontSize: TYPOGRAPHY.headings.sizes.h1,
              fontWeight: TYPOGRAPHY.weight.bold,
              marginBottom: SPACING[16]
            }}>Bulk Upload Videos</h2>
            <p style={{
              fontSize: TYPOGRAPHY.body.sizes.small,
              color: COLORS.text.secondary,
              marginBottom: SPACING[8]
            }}>
              Select multiple video files to upload. Videos will be uploaded directly to S3 storage.
            </p>
            <div style={{
              marginBottom: SPACING[16],
              padding: SPACING[12],
              backgroundColor: '#ECFDF5',
              border: '1px solid #A7F3D0',
              borderRadius: RADIUS
            }}>
              <p style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: '#065F46' }}>
                <strong>Direct Upload:</strong> Videos uploaded directly to S3 (no server memory issues)
              </p>
              <p style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: '#065F46', marginTop: SPACING[4] }}>
                <strong>Limit:</strong> Maximum 100 videos per batch
              </p>
            </div>
            <input
              type="file"
              multiple
              accept="video/*"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleBulkUploadSubmit(e.target.files);
                }
              }}
              disabled={isUploading}
              style={{
                width: '100%',
                padding: SPACING[8],
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                opacity: isUploading ? 0.5 : 1
              }}
            />
            {isUploading && (
              <div style={{ marginTop: SPACING[16] }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: SPACING[8]
                }}>
                  <Spinner size="md" />
                  <span style={{
                    fontSize: TYPOGRAPHY.body.sizes.small,
                    color: COLORS.text.secondary,
                    marginLeft: SPACING[8]
                  }}>{uploadStatus}</span>
                </div>
                {Object.keys(uploadProgress).length > 0 && totalFiles > 0 && (
                  <div style={{
                    marginTop: SPACING[8],
                    fontSize: TYPOGRAPHY.body.sizes.tiny,
                    color: COLORS.text.secondary,
                    textAlign: 'center'
                  }}>
                    {Object.keys(uploadProgress).length} of {totalFiles} files uploaded...
                  </div>
                )}
              </div>
            )}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: SPACING[8],
              marginTop: SPACING[24]
            }}>
              <button
                onClick={() => {
                  setIsBulkUploadOpen(false);
                  setBulkUploadModuleId(null);
                }}
                disabled={isUploading}
                style={{
                  paddingLeft: SPACING[16],
                  paddingRight: SPACING[16],
                  paddingTop: SPACING[8],
                  paddingBottom: SPACING[8],
                  backgroundColor: hoveredCancelBtn ? COLORS.border.hover : COLORS.border.default,
                  borderRadius: RADIUS,
                  border: 'none',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  opacity: isUploading ? 0.5 : 1
                }}
                onMouseEnter={() => setHoveredCancelBtn(true)}
                onMouseLeave={() => setHoveredCancelBtn(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseList;
